import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/react-hooks";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import humanMaleFemale from "@iconify/icons-mdi/human-male-female";
import { useMyHrContractsByEmployee } from "my-hr/hooks";
import MyTaxApplicationCardInfoItem from "my-tax/components/application-card-info-item/application-card-info-item";
import ApplicationCollapseHeader from "my-tax/components/application-collapse-header";
import MyTaxApplicationHeader from "my-tax/components/application-header/application-header";
import DependentsFourChildrenCollapse from "my-tax/components/dependents-four-children-collapse";
import MyTaxTwoOrMoreChildrenTaxCreditApplicationPrint from "my-tax/components/two-or-more-children-tax-credit-application-print/two-or-more-children-tax-credit-application-print";
import MyTaxModifiedStatementCheck from "my-tax/components/modified-statement-check/modified-statement-check";
import TaxCreditApplicationButtonBars from "my-tax/components/tax-credit-application-buttonbars/tax-credit-application-buttonbars";
import MyTaxCreditApplicationCheckboxLine from "my-tax/components/tax-credit-application-checkbox-line/tax-credit-application-checkbox-line";
import MyTaxCreditApplicationErrorBox from "my-tax/components/tax-credit-application-error-box/tax-credit-application-error-box";
import { NOT_PROVIDED } from "my-tax/constants";
import { MY_TAX_GET_THREE_CHILDREN_DECLARATION } from "my-tax/graphql";
import { MY_TAX_CREATE_TWO_CHILDREN_DECLARATION } from "my-tax/graphql";
import { taxDeclarationThreeChildren } from "my-tax/states/tax-declaration-three-children";
import { OcCard, OcDownload, OcMonthSelect } from "oc/components";
import {
  useOcDependentsData as useDependentsDataDI,
  useOcCurrentUserState,
  useOcSnackbar,
} from "oc/hooks";
import { useTheme } from "@mui/material/styles";
import { childrenCountState } from "my-tax/states/children-count-state";

type UseFormDataProps = {
  _id: number | string | undefined;
  setSelected: (params: any) => void;
};

const useFormData = ({ _id, setSelected }: UseFormDataProps) => {
  const { t } = useTranslation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [disabledButton, setDisabledButton] = useState(false);
  const [states, setStates] = useRecoilState(taxDeclarationThreeChildren);
  const resetStates = useResetRecoilState(taxDeclarationThreeChildren);

  const { data, loading, error } = useQuery(
    MY_TAX_GET_THREE_CHILDREN_DECLARATION,
    {
      fetchPolicy: "cache-and-network",
      skip: _id == null,
      variables: {
        _id: Number.parseInt(_id == null ? "0" : String(_id), 10),
      },
    },
  );

  const result = data?.taxDeclarationThreeChildren;

  // Sync query result to Recoil state (replaces deprecated onCompleted)
  useEffect(() => {
    if (!loading && result) {
      const newStates = { ...result };
      newStates.dateOfDeclaration = new Date(result?.dateOfDeclaration);
      delete newStates.__typename;
      setStates(newStates);
    }
  }, [loading, result, setStates]);

  const handleChange = ({ key, value }: { key: string; value: any }) => {
    const newState = { ...states, [key]: value };
    setStates(newState);
  };

  const { setOpenMessage } = useOcSnackbar();
  const [saveDeclaration, { loading: mutationLoading }] = useMutation(
    MY_TAX_CREATE_TWO_CHILDREN_DECLARATION,
    {
      onCompleted: (data) => {
        const res = data?.createTaxDeclarationTwoChildren;
        setDisabledButton(false);
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (
              message === "A korábban beadott nyilatkozata törlésre került!"
            ) {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
            });
          }
          setSaveErrors([]);
          const params = {
            editable: false,
            tableId: res?.tableId,
            year: states?.dateOfDeclaration?.getFullYear().toString(),
            type: "TaxDeclarationThreeChildren",
            state: "requested",
          };
          setSelected(params);
        } else {
          setSaveErrors(res?.messages);
          setDisabledButton(false);
        }
      },
      onError: () => {
        setSaveErrors([t("MY_TAX_DECLARATION_CREATED_FAILED")]);
        setDisabledButton(false);
      },
    },
  );

  useEffect(() => {
    if (!states?.isModifiedDeclaration) {
      handleChange({ key: "notApplyFromMonth", value: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states?.isModifiedDeclaration]);

  const handleStateChange = (newState: any) => {
    setStates(newState);
  };

  const handleSave = () => {
    setDisabledButton(true);
    const params = { ...states };
    saveDeclaration({ variables: params });
  };

  return {
    data: result,
    loading: loading || mutationLoading,
    error,
    states,
    disabledButton,
    resetStates,
    handleChange,
    handleSave,
    saveErrors,
    setSaveErrors,
    handleStateChange,
  };
};

type MyTaxTwoOrMoreChildrenTaxCreditApplicationProps = {
  fullName: string;
  taxNumber: string;
  useDependentsData?: typeof useDependentsDataDI;
  taxApplicationSaveEnabled: boolean;
  handleRemoveRequest?: () => void;
  selected: any;
  setSelected: (params: any) => void;
  isMobile: boolean;
};

const MyTaxTwoOrMoreChildrenTaxCreditApplication = ({
  fullName,
  taxNumber,
  useDependentsData = useDependentsDataDI,
  taxApplicationSaveEnabled,
  handleRemoveRequest,
  selected,
  setSelected,
  isMobile,
}: MyTaxTwoOrMoreChildrenTaxCreditApplicationProps) => {
  const editable = selected?.editable;
  const history = useHistory();
  const theme = useTheme();

  const {
    states,
    loading,
    error,
    saveErrors,
    disabledButton,
    setSaveErrors,
    resetStates,
    handleChange,
    handleSave,
    handleStateChange,
  } = useFormData({
    _id: selected?.tableId,
    setSelected,
  });

  const isModified = selected?.isModified;

  useEffect(() => {
    if (isModified && !states?.isModifiedDeclaration) {
      handleStateChange({ ...states, isModifiedDeclaration: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModified, states?.isModifiedDeclaration]);

  const showModifiedButton = states?.state === "E";
  const showRemoveButton =
    states?.state === "K" || selected?.state === "K";

  const dateOfDeclarationString = new Date(
    states?.dateOfDeclaration,
  ).toLocaleDateString();

  // Date-based logic: use new version if declaration date is 2026-01-01 or later
  const isNewVersion = useMemo(() => {
    if (!states?.dateOfDeclaration) return false;
    const declarationDate = new Date(states.dateOfDeclaration);
    return declarationDate >= new Date("2026-01-01T00:00:00.000Z");
  }, [states?.dateOfDeclaration]);

  const title = isNewVersion
    ? "Adóelőleg-nyilatkozat a két, a három, illetve a négy vagy több gyermeket nevelő anyák kedvezményének érvényesítéséről "
    : "Adóelőleg-nyilatkozat a három gyermeket nevelő anyák és a négy vagy több gyermeket nevelő anyák kedvezményének érvényesítéséről ";

  const componentRef = useRef(null);

  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  const handlePrint = () => {
    print();
  };

  const { data: dependents } = useDependentsData();

  useEffect(() => {
    if (editable) {
      for (const item of dependents ?? []) {
        const dateOfBirth = item.birthDate ? new Date(item.birthDate) : null;
        if (dateOfBirth && dateOfBirth.toString() !== "Invalid Date") {
          const newItem = {
            dependentSerialNumber: item.itemSerial,
            name: item.name,
            taxNumber: item.taxNumber,
            placeOfBirth: item.birthPlace,
            dateOfBirth: dateOfBirth.toLocaleDateString(),
            mothersName: item.mothersName,
          };
          const isExist = states?.dependents.find(
            (d: any) =>
              d.dependentSerialNumber === newItem.dependentSerialNumber,
          );
          if (!isExist) {
            const dep = states?.dependents;
            const newDependents = [...dep, newItem];
            handleChange({ key: "dependents", value: newDependents });
          }
        }
      }
    }
  }, [dependents, editable, handleChange, states?.dependents]);

  const { t } = useTranslation();

  const [childrenCountGroupState, setChildrenCountGroupState] = useRecoilState(
    childrenCountState,
  );

  const resetChildrenCountGroupState = useResetRecoilState(childrenCountState);

  const handleCancel = () => {
    // Only reset states when in edit mode
    if (editable) {
      resetChildrenCountGroupState();
      resetStates();
    }
    setSaveErrors([]);
  };

  useEffect(() => {
    if (states?.isModifiedDeclaration === false && states?.notApplyFromMonth) {
      handleChange({ key: "notApplyFromMonth", value: "" });
    }

    let next = "";
    if (states?.isEligibleTwo) {
      next = "twoChildren";
    } else if (states?.isEligibleThree) {
      next = "threeChildren";
    } else if (states?.isEligible) {
      next = "fourOrMoreChildren";
    }

    if (childrenCountGroupState !== next) {
      setChildrenCountGroupState(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    states?.isModifiedDeclaration,
    states?.isEligibleTwo,
    states?.isEligibleThree,
    states?.isEligible,
    states?.notApplyFromMonth,
    childrenCountGroupState,
    handleChange,
  ]);

  useEffect(() => {
    return () => {
      resetChildrenCountGroupState();
      resetStates();
    };
  }, [resetStates, resetChildrenCountGroupState]);

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  let selectedContract = contractsData?.find(
    (item: any) => item.code === contractCode,
  );

  let disabled = true;

  const handleModChange = ({ key, value }: { key: string; value: any }) => {
    handleChange({ key, value });
  };

  const handleRadioGroupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "twoChildren") {
      const newStates = { ...states, isEligibleTwo: true, isEligibleThree: false, isEligible: false };
      handleStateChange(newStates);
    } else if (e.target.value === "threeChildren") {
      const newStates = { ...states, isEligibleTwo: false, isEligibleThree: true, isEligible: false };
      handleStateChange(newStates);
    } else {
      const newStates = { ...states, isEligibleTwo: false, isEligibleThree: false, isEligible: true };
      handleStateChange(newStates);
    }
    setChildrenCountGroupState(e.target.value);
  };

  const handleScrollToError = () => {
    const errorElement = document.getElementById("errorBox");
    if (errorElement) {
      window.scrollTo({
        top: 300,
        behavior: "smooth",
      });
    }
  };
  useLayoutEffect(() => {
    handleScrollToError();
  }, [saveErrors, history.action]);

  useEffect(() => {
    // Only reset when creating a NEW declaration (editable=true AND no tableId)
    if (editable && !selected?.tableId) {
      resetStates();
    }
    if (history.action === "POP" && editable) {
      return handleCancel();
    }
  }, []); //eslint-disable-line

  return (
    <Box
      style={{
        marginBottom: "-15px",
      }}
    >
      <Box
        sx={{
          display: "none",
          displayPrint: "block",
          paddingRight: "85px",
        }}
      >
        <MyTaxTwoOrMoreChildrenTaxCreditApplicationPrint
          ref={componentRef}
          fullName={fullName}
          taxNumber={taxNumber}
          employerName={selectedContract?.company?.name || ""}
          employerTaxNumber={selectedContract?.company?.taxNumber || ""}
          states={states}
          applicationDate={dateOfDeclarationString}
          isNewVersion={isNewVersion}
        />
      </Box>
      <MyTaxApplicationHeader
        title={title}
        onCancel={handleCancel}
        isMobile={isMobile}
      />
      <Box
        padding="16px"
        display="flex"
        flexDirection="row"
        justifyContent="center"
      >
        <Box
          width="260px"
          style={{
            padding: "8px",
            borderRadius: "4px",
          }}
        >
          <OcDownload
            label="Tájékoztató"
            iconType="document"
            actionIconType="url"
            href={"#/my-hr/three-children-or-more-tax-credit-information"}
            target="_blank"
            isSmall={true}
          />
        </Box>
      </Box>
      {saveErrors.length > 0 && (
        <Box id="errorBox">
          <MyTaxCreditApplicationErrorBox error={saveErrors} />
        </Box>
      )}
      <OcCard
        label="I. A nyilatkozatot adó magánszemély"
        loading={loading}
        error={!!error}
      >
        <Box
          style={{
            padding: "0px 17px",
          }}
        >
          <Box height="52px" />
          <MyTaxApplicationCardInfoItem
            label="Dátum"
            value={dateOfDeclarationString}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Dolgozó neve"
            value={fullName}
            isMobile={isMobile}
          />
          <MyTaxApplicationCardInfoItem
            label="Adóazonosító jele"
            value={taxNumber}
            isMobile={isMobile}
          />
        </Box>
        <Box height="15px" />
        <MyTaxModifiedStatementCheck
          label={t("MY_TAX_MODIFIED_APPLICATION")}
          name="isModifiedDeclaration"
          value={states?.isModifiedDeclaration}
          onChange={handleModChange}
          disabled={!editable || isModified}
          isMobile={isMobile}
        />
        <Box height="15px" />
        <Box display="flex" flexDirection="row">
          <Typography variant="body1" color={editable ? "primary" : "info"}>
            1.
          </Typography>
          <Box width="16px" />
          <Typography variant="body1" color={editable ? "primary" : "info"}>
            Gyermek neve, adóazonosító jele
          </Typography>
        </Box>
        <Box height="36px" />
        {states?.dependents.length !== 0 ? (
          states?.dependents?.map((item: any, index: number) => {
            return (
              <Box key={`dependents-${index}`}>
                <DependentsFourChildrenCollapse
                  index={index}
                  data={item}
                  isMobile={isMobile}
                />
                <Box height="36px" />
              </Box>
            );
          })
        ) : (
          <Box>
            <ApplicationCollapseHeader
              dependentName={NOT_PROVIDED}
              dependentTaxNumber=""
              expanded={disabled}
              icon={humanMaleFemale}
              taxNumberLabel=""
              handleExpandClick={() => { }}
              isMobile={isMobile}
            />
            <Box height="36px" />
          </Box>
        )}
        <Box display="flex" flexDirection="row">
          <Typography variant="body1" color={editable ? "primary" : "info"}>
            2.
          </Typography>
          <Box width="16px" />
          <Typography variant="body1" color={editable ? "primary" : "info"}>
            Nyilatkozom, hogy jogosult vagyok, és igénybe kívánom venni
          </Typography>
        </Box>
        <FormControl
          component="fieldset"
          sx={{
            marginLeft: "30px",
            alignItems: "flex-start",
            display: "flex",
          }}
        >
          <Box height="8px" />
          <RadioGroup
            value={childrenCountGroupState}
            aria-label="children"
            name="controlled-radio-buttons-group"
            onChange={handleRadioGroupChange}
          >
            {isNewVersion && (
              <>
                <FormControlLabel
                  value="twoChildren"
                  control={
                    <Radio
                      id="twoChildren"
                      style={{
                        color: !editable
                          ? theme.palette.gray60.main
                          : theme.palette.darkBase.main,
                      }}
                      disabled={!editable}
                    />
                  }
                  label={
                    <Typography
                      variant="body1"
                      color={editable ? "primary" : "info"}
                      style={{ alignItems: "center" }}
                    >
                      a két gyermeket nevelő anyák kedvezményét
                    </Typography>
                  }
                />
                <Box height="3px" />
              </>
            )}
            <FormControlLabel
              value="threeChildren"
              control={
                <Radio
                  id="threeChildren"
                  style={{
                    color: !editable
                      ? theme.palette.gray60.main
                      : theme.palette.darkBase.main,
                  }}
                  disabled={!editable}
                />
              }
              label={
                <Typography
                  variant="body1"
                  color={editable ? "primary" : "info"}
                  style={{ alignItems: "center" }}
                >
                  a három gyermeket nevelő anyák kedvezményét
                </Typography>
              }
            />
            <Box height="3px" />
            <FormControlLabel
              value="fourOrMoreChildren"
              control={
                <Radio
                  id="fourOrMoreChildren"
                  style={{
                    color: !editable
                      ? theme.palette.gray60.main
                      : theme.palette.darkBase.main,
                  }}
                  disabled={!editable}
                />
              }
              label={
                <Typography
                  variant="body1"
                  color={editable ? "primary" : "info"}
                  style={{ alignItems: "center" }}
                >
                  a négy vagy több gyermeket nevelő anyák kedvezményét
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>
        <Box height="25px" />
        <MyTaxCreditApplicationCheckboxLine
          id="isApplyNetakPermanently"
          type="checkbox"
          prefix="3."
          checked={states?.permanentlyEntitled}
          checkboxDisabled={!editable}
          onChange={(e) =>
            handleChange({
              key: "permanentlyEntitled",
              value: e.target.checked,
            })
          }
          text="A 2. pontban jelölt kedvezményt a nyilatkozatom visszavonásáig kérem figyelembe venni (folytatólagos nyilatkozatot teszek)"
          editable={editable}
        />
        <Box height="36px" />
        <Box>
          <Box display="flex" flexDirection="row">
            <Typography
              variant="body1"
              color={
                editable && states?.isModifiedDeclaration ? "primary" : "info"
              }
            >
              4.
            </Typography>
            <Box width="16px" />
            <Typography
              variant="body1"
              color={
                editable && states?.isModifiedDeclaration ? "primary" : "info"
              }
            >
              {isNewVersion
                ? "Nyilatkozom, hogy a két gyermeket nevelő/ a három gyermeket nevelő anyák/ a négy vagy több gyermeket nevelő anyák kedvezményét"
                : "Nyilatkozom, hogy a három gyermeket nevelő anyák/négy vagy több gyermeket nevelő anyák kedvezményét"
              }
            </Typography>
          </Box>
          <Box height="16px" />
          <Box
            display="flex"
            flexDirection={isMobile ? "column" : "row"}
            alignItems="center"
            style={{ marginLeft: isMobile ? "0px" : "30px" }}
          >
            <OcMonthSelect
              id="notApplyFromMonth"
              startMonth={1}
              width="160px"
              label={t("MY_TAX_TAX_CREDIT_MONTH")}
              disabled={!states?.isModifiedDeclaration || !editable}
              selectedMonth={states?.notApplyFromMonth}
              onSelectMonth={(e) =>
                handleChange({ key: "notApplyFromMonth", value: e })
              }
              monthList={[
                { index: "1", name: "Január" },
                { index: "2", name: "Február" },
                { index: "3", name: "Március" },
                { index: "4", name: "Április" },
                { index: "5", name: "Május" },
                { index: "6", name: "Június" },
                { index: "7", name: "Július" },
                { index: "8", name: "Augusztus" },
                { index: "9", name: "Szeptember" },
                { index: "10", name: "Október" },
                { index: "11", name: "November" },
                { index: "12", name: "December" },
              ]}
            />
            <Box width="16px" />
            <Typography
              variant="body1"
              color={
                editable && states?.isModifiedDeclaration ? "primary" : "info"
              }
            >
              hónaptól kezdődően nem (erre a hónapra sem) kívánom igénybe
              venni.
            </Typography>
          </Box>
        </Box>
        <Box height="36px" />
      </OcCard>
      <Box height="16px" />
      <TaxCreditApplicationButtonBars
        taxApplicationSaveEnabled={editable && taxApplicationSaveEnabled}
        cancelData={handleCancel}
        handlePrint={handlePrint}
        saveData={handleSave}
        editable={editable}
        disabledButton={disabledButton}
        handleRemoveRequest={handleRemoveRequest ?? (() => { })}
        showModifiedButton={showModifiedButton}
        showRemoveButton={showRemoveButton}
        isModified={isModified}
        isModifiedDeclaration={states?.isModifiedDeclaration}
        isMobile={isMobile}
      />
    </Box>
  );
};

export default MyTaxTwoOrMoreChildrenTaxCreditApplication;
